// src/configs/initializer.js

/**
 * @file initializer.js
 * @description Source of truth for APPLICATIONS and FORCE_APP selection.
 *
 * Configuration is controlled by .env variables (similar to backend):
 * - VITE_APPLICATION_CODE: Which app to load (applekkus, appmulti, etc.)
 * - VITE_RUNTIME_MODE: Which backend to connect (production, homologation)
 *
 * In localhost, uses VITE_APPLICATION_CODE from .env
 * In production domains, selection is strictly by DOMAINS array.
 */

import appmulti from './apps/appmulti/appmulti.js';
import applekkus from './apps/applekkus/applekkus.js';
import appfunnel from './apps/appfunnel/appfunnel.js';
import appsindico from './apps/appsindico/appsindico.js';
import appcaup from './apps/appcaup/appcaup.js';

/**
 * @typedef {Object} AppMeta
 * @property {string} ID
 * @property {string} NAME
 * @property {string[]} [DOMAINS]
 * @property {Record<string, any>} [FIREBASE_CONFIG]
 * @property {string} [THEME_COLOR]
 * @property {string} [THEME]
 * @property {string} [PROFILE]
 * @property {boolean} [HOMOLOGATION_MODE]
 */

// All available applications
export const APPLICATIONS = [appmulti, applekkus, appfunnel, appsindico, appcaup];

// Map of application codes to their config objects
const APP_MAP = {
  applekkus,
  appmulti,
  appfunnel,
  appsindico,
  appcaup,
};

/**
 * Detects localhost environment based on hostname.
 * @returns {boolean}
 */
function isLocalHost() {
  if (typeof window === 'undefined') return false;
  const h = (window.location && window.location.hostname) || '';
  return (
    h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0' || h === '::1' || /\.local$/i.test(h)
  );
}

const IS_LOCALHOST = isLocalHost();

/**
 * Get the forced app based on environment configuration.
 * Priority:
 * 1) window.__FORCE_APP__ (runtime override)
 * 2) VITE_APPLICATION_CODE from .env
 * 3) Default to applekkus
 *
 * In production domains, returns null (domain-based selection only).
 */
function getForceApp() {
  if (!IS_LOCALHOST) return null;

  // 1) Check for runtime override
  const windowForce = typeof window !== 'undefined' ? window.__FORCE_APP__ : null;
  if (windowForce) return windowForce;

  // 2) Check VITE_APPLICATION_CODE from .env
  const appCode = import.meta.env.VITE_APPLICATION_CODE;
  if (appCode && APP_MAP[appCode]) {
    return APP_MAP[appCode];
  }

  // 3) Default fallback
  return applekkus;
}

/**
 * @type {AppMeta | string | null}
 */
export const FORCE_APP = getForceApp();
